import { Link } from 'react-router-dom';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Lock, Play, ShoppingBag } from 'lucide-react';
import {
  COACHING_BREATHWORK_OUTCOME_LABELS,
  type CoachingBreathworkOutcome,
} from '@mlm/shared/coaching';
import type { MyCourse } from '@mlm/shared';
import { cn } from '@mlm/ui/lib/cn';
import { learn, type FrequencyCategory, type Today } from '../../lib/api';
import {
  breathMeta,
  mantraIntention,
  mantraMeta,
  meditationMeta,
  pickToolsForToday,
  softestAxis,
  trackMeta,
} from '../../lib/home';
import { qk, useFeatureLabel } from '../../lib/queries';
import type { HomeCollection, HomeLibraries } from '../../lib/use-home-libraries';
import { BreathPatternRing, Rail, SectionHead, StarGlyph } from '../visual';

/*
 * Home's collections (the redesign's "tools" half): one rail per tool on a
 * phone, wider rails and three short columns on desktop. Every tile opens the
 * tool's existing route; nothing here records a practice.
 */

const focusRing =
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-verde';
const tileMono = 'block font-mono uppercase tracking-[0.12em]';

const outcomeLabel = (o: string) =>
  COACHING_BREATHWORK_OUTCOME_LABELS[o as CoachingBreathworkOutcome] ?? o;

const categoryLabel = (cats: FrequencyCategory[], key: string) =>
  cats.find((c) => c.key === key)?.label ?? key;

/** A tool the member's plan does not open: one tile where the rail would be, naming the plan. */
function LockTile({
  collection,
  to,
  noun,
  className,
}: {
  collection: HomeCollection<unknown>;
  to: string;
  noun: string;
  className?: string;
}) {
  const plan = useFeatureLabel(collection.feature);
  return (
    <Link
      to={to}
      className={cn(
        'flex flex-col justify-end rounded-[18px] border border-dashed border-[rgba(16,32,26,0.25)] bg-surface p-3.5 text-ink',
        focusRing,
        className,
      )}
    >
      <Lock className="h-5 w-5 text-gold" strokeWidth={1.5} aria-hidden />
      <span className="mt-2.5 block text-sm font-medium">
        {noun} with {plan}
      </span>
      <span className="mt-0.5 block text-xs text-sage-deep">See what it includes</span>
    </Link>
  );
}

/** The two decorative rings in a forest tile's top-right corner. */
function TileRings({ size }: { size: 'sm' | 'lg' }) {
  return (
    <>
      <span
        aria-hidden
        className={cn(
          'absolute rounded-full border border-paper/[0.16]',
          size === 'sm'
            ? '-right-[26px] -top-[26px] h-[110px] w-[110px]'
            : '-right-[30px] -top-[30px] h-[130px] w-[130px]',
        )}
      />
      <span
        aria-hidden
        className={cn(
          'absolute rounded-full border border-paper/[0.12]',
          size === 'sm'
            ? '-right-1.5 -top-1.5 h-[70px] w-[70px]'
            : '-right-2 -top-2 h-[84px] w-[84px]',
        )}
      />
    </>
  );
}

// ── Tools for today (phone) ─────────────────────────────────────────────────

/** "Picked for a gentle Relationships day": one of each tool, fitted to the softest axis. */
export function TodayToolsRail({ today, libraries }: { today: Today; libraries: HomeLibraries }) {
  const tools = pickToolsForToday({
    alignment: today.alignment,
    patterns: libraries.breath.items,
    tracks: libraries.frequencies.items,
    mantras: libraries.mantras.items,
    meditations: libraries.meditations.items,
  });
  if (tools.length === 0) return null;
  const soft = today.alignment ? softestAxis(today.alignment) : null;
  return (
    <>
      <div className="px-5 pt-[30px]">
        <p className="eyebrow text-[0.62rem]">Tools for today</p>
        <h2 className="mt-1.5 text-pretty font-display text-[22px] font-medium leading-[1.2] text-ink">
          {soft ? `Picked for a ${soft.band.toLowerCase()} ${soft.label} day` : 'Picked for today'}
        </h2>
      </div>
      <Rail className="mt-3">
        {tools.map((t) => (
          <Link
            key={`${t.kind}:${t.id}`}
            to={t.to}
            className={cn(
              'bg-forest-tile relative h-[132px] w-[168px] overflow-hidden rounded-[18px] text-paper shadow-[0_10px_24px_-14px_rgba(16,32,26,0.5)]',
              focusRing,
            )}
          >
            <span
              aria-hidden
              className="absolute -right-[30px] -top-[30px] h-[110px] w-[110px] rounded-full border border-paper/[0.16]"
            />
            <span
              className={cn(
                tileMono,
                'absolute left-3.5 top-3 text-[0.54rem] tracking-[0.16em] text-paper/75',
              )}
            >
              {t.kind}
            </span>
            <span className="absolute inset-x-3.5 bottom-3">
              <span className="line-clamp-2 font-display text-[17px] font-medium leading-[1.15]">
                {t.title}
              </span>
              <span className={cn(tileMono, 'mt-1 truncate text-[0.56rem] text-paper/70')}>
                {t.meta}
              </span>
            </span>
          </Link>
        ))}
      </Rail>
    </>
  );
}

// ── Breathe ─────────────────────────────────────────────────────────────────

export function BreatheRail({
  libraries,
  layout,
}: {
  libraries: HomeLibraries;
  layout: 'phone' | 'desktop';
}) {
  const c = libraries.breath;
  if (c.state === 'off' || (c.state === 'on' && c.items.length === 0)) return null;
  const phone = layout === 'phone';
  return (
    <section className={phone ? 'mt-[22px]' : 'mt-[22px]'}>
      <SectionHead
        title="Breathe"
        size={phone ? 'phone' : 'desktop'}
        className={phone ? 'px-5' : 'pb-3'}
        link={{ label: 'How do you want to feel?', to: '/align/breathwork' }}
      />
      <Rail gap={phone ? 'md' : 'lg'} bleed={phone}>
        {c.state === 'locked' ? (
          <LockTile collection={c} to="/align/breathwork" noun="Breathwork" className="w-[160px]" />
        ) : (
          c.items.map((p) => (
            <Link
              key={p.id}
              to={`/align/breathwork/${encodeURIComponent(p.id)}`}
              className={cn(
                'rounded-[18px] border border-border bg-surface text-ink',
                phone ? 'w-[140px] p-3.5' : 'w-[168px] rounded-[20px] p-4',
                focusRing,
              )}
            >
              <BreathPatternRing pattern={p.pattern} size={phone ? 64 : 72} />
              <span
                className={cn(
                  'mt-3 block font-medium leading-tight',
                  phone ? 'text-sm' : 'text-[15px]',
                )}
              >
                {p.title}
              </span>
              <span className={cn(tileMono, 'mt-1 text-[0.56rem] text-sage-deep')}>
                {breathMeta(p)}
              </span>
              <span
                className={cn(
                  tileMono,
                  'mt-2.5 inline-block rounded-full bg-wash px-2 py-1 text-[0.54rem] text-verde',
                )}
              >
                {outcomeLabel(p.outcome)}
              </span>
            </Link>
          ))
        )}
      </Rail>
    </section>
  );
}

// ── Frequencies ─────────────────────────────────────────────────────────────

export function FrequenciesRail({
  libraries,
  layout,
}: {
  libraries: HomeLibraries;
  layout: 'phone' | 'desktop';
}) {
  const c = libraries.frequencies;
  if (c.state === 'off' || (c.state === 'on' && c.items.length === 0)) return null;
  const phone = layout === 'phone';
  return (
    <section className={phone ? 'mt-[22px]' : 'mt-[30px]'}>
      <SectionHead
        title="Frequencies"
        size={phone ? 'phone' : 'desktop'}
        className={phone ? 'px-5' : 'pb-3'}
        link={{
          label: c.items.length > 0 ? `See all ${c.items.length}` : 'See all',
          to: '/align/frequencies',
        }}
      />
      <Rail gap={phone ? 'md' : 'lg'} bleed={phone}>
        {c.state === 'locked' ? (
          <LockTile
            collection={c}
            to="/align/frequencies"
            noun="Frequencies"
            className="h-[196px] w-[150px]"
          />
        ) : (
          c.items.map((t) => (
            <Link
              key={t.id}
              to={`/align/frequencies/${t.id}`}
              className={cn(
                'bg-forest-tile relative overflow-hidden text-paper',
                phone
                  ? 'h-[196px] w-[150px] rounded-[18px] shadow-[0_10px_24px_-14px_rgba(16,32,26,0.5)]'
                  : 'h-[224px] w-[184px] rounded-[20px] shadow-[0_12px_28px_-16px_rgba(16,32,26,0.5)]',
                focusRing,
              )}
            >
              <TileRings size={phone ? 'sm' : 'lg'} />
              <span
                className={cn(
                  tileMono,
                  'absolute tracking-[0.16em] text-paper/75',
                  phone ? 'left-3.5 top-3.5 text-[0.56rem]' : 'left-4 top-4 text-[0.58rem]',
                )}
              >
                {categoryLabel(libraries.frequencyCategories, t.category)}
              </span>
              <span
                aria-hidden
                className={cn(
                  'absolute flex items-center justify-center rounded-full border border-paper/35 bg-paper/[0.16]',
                  phone ? 'right-3 top-10 h-[30px] w-[30px]' : 'right-3.5 top-11 h-8 w-8',
                )}
              >
                <Play className="ml-0.5 h-3 w-3" fill="currentColor" aria-hidden />
              </span>
              <span
                className={cn('absolute', phone ? 'inset-x-3.5 bottom-3.5' : 'inset-x-4 bottom-4')}
              >
                <span
                  className={cn(
                    'line-clamp-3 font-display font-medium leading-[1.15]',
                    phone ? 'text-[17px]' : 'text-xl',
                  )}
                >
                  {t.title}
                </span>
                <span
                  className={cn(
                    tileMono,
                    'truncate text-paper/70',
                    phone ? 'mt-1.5 text-[0.58rem]' : 'mt-2 text-[0.6rem]',
                  )}
                >
                  {trackMeta(t)}
                </span>
              </span>
            </Link>
          ))
        )}
      </Rail>
    </section>
  );
}

// ── Meditate, Mantras, Toolkit: rails on a phone, columns on desktop ───────

/** Concentric rings for a meditation tile: a quiet circle to settle into. */
function SitRings() {
  return (
    <>
      <span
        aria-hidden
        className="absolute -right-[30px] -top-[30px] h-[120px] w-[120px] rounded-full border border-verde/25"
      />
      <span
        aria-hidden
        className="absolute -right-3 -top-3 h-[84px] w-[84px] rounded-full border border-verde/25"
      />
      <span
        aria-hidden
        className="absolute right-1.5 top-1.5 h-12 w-12 rounded-full border border-verde/30 bg-white/40"
      />
    </>
  );
}

export function MeditateRail({ libraries }: { libraries: HomeLibraries }) {
  const c = libraries.meditations;
  if (c.state === 'off' || (c.state === 'on' && c.items.length === 0)) return null;
  return (
    <section className="mt-[22px]">
      <SectionHead
        title="Meditate"
        className="px-5"
        link={{ label: 'See all', to: '/align/meditations' }}
      />
      <Rail>
        {c.state === 'locked' ? (
          <LockTile
            collection={c}
            to="/align/meditations"
            noun="Meditation"
            className="h-[116px] w-[176px]"
          />
        ) : (
          c.items.map((m) => (
            <Link
              key={m.id}
              to={`/align/meditations/${encodeURIComponent(m.id)}`}
              className={cn(
                'bg-meditate-wash relative h-[116px] w-[156px] overflow-hidden rounded-[18px] border border-verde/[0.12] text-ink',
                focusRing,
              )}
            >
              <SitRings />
              <span className="absolute inset-x-3.5 bottom-3">
                <span className="line-clamp-2 font-display text-base font-medium leading-[1.15]">
                  {m.title}
                </span>
                <span className={cn(tileMono, 'mt-1 text-[0.56rem] text-sage')}>
                  {meditationMeta(m)}
                </span>
              </span>
            </Link>
          ))
        )}
      </Rail>
    </section>
  );
}

export function MantrasRail({ libraries }: { libraries: HomeLibraries }) {
  const c = libraries.mantras;
  if (c.state === 'off' || (c.state === 'on' && c.items.length === 0)) return null;
  return (
    <section className="mt-[22px]">
      <SectionHead
        title="Mantras"
        className="px-5"
        link={{ label: 'See all', to: '/align/mantras' }}
      />
      <Rail>
        {c.state === 'locked' ? (
          <LockTile
            collection={c}
            to="/align/mantras"
            noun="Mantras"
            className="h-[116px] w-[176px]"
          />
        ) : (
          c.items.map((m) => (
            <Link
              key={m.id}
              to={`/align/mantras/${encodeURIComponent(m.id)}`}
              className={cn(
                'flex h-[116px] w-[176px] flex-col justify-between rounded-[18px] border border-verde/[0.14] bg-wash p-3.5 text-ink',
                focusRing,
              )}
            >
              <span className="line-clamp-2 font-display text-lg font-normal italic leading-[1.15] text-[#8A7342]">
                {m.title}
              </span>
              <span>
                <span className="line-clamp-1 text-xs text-ink/75">{mantraIntention(m)}</span>
                <span className={cn(tileMono, 'mt-0.5 text-[0.54rem] text-sage')}>
                  {mantraMeta(m)}
                </span>
              </span>
            </Link>
          ))
        )}
      </Rail>
    </section>
  );
}

export function ToolkitRail({ libraries }: { libraries: HomeLibraries }) {
  const c = libraries.toolkit;
  if (c.state === 'off' || (c.state === 'on' && c.items.length === 0)) return null;
  return (
    <section className="mt-[22px]">
      <SectionHead
        title="Toolkit"
        className="px-5"
        link={{ label: 'See all', to: '/manifest/toolkit' }}
      />
      <Rail>
        {c.state === 'locked' ? (
          <LockTile
            collection={c}
            to="/manifest/toolkit"
            noun="The toolkit"
            className="h-[100px] w-[176px]"
          />
        ) : (
          c.items.map((p) => (
            <Link
              key={p.id}
              to={`/manifest/toolkit/${encodeURIComponent(p.id)}`}
              className={cn(
                'bg-toolkit-wash relative flex h-[100px] w-[150px] flex-col justify-end overflow-hidden rounded-[18px] border border-gold/25 p-3.5 text-ink',
                focusRing,
              )}
            >
              <span className="absolute right-3 top-3">
                <StarGlyph size={22} />
              </span>
              <span className="line-clamp-2 font-display text-base font-medium leading-[1.15]">
                {p.title}
              </span>
              <span className={cn(tileMono, 'mt-1 text-[0.56rem] text-[#8A7342]')}>
                {p.minutes} min
              </span>
            </Link>
          ))
        )}
      </Rail>
    </section>
  );
}

/** Desktop: Meditate, Mantras and Toolkit & Learn as three short columns. */
export function PracticeColumns({ libraries }: { libraries: HomeLibraries }) {
  const meditations = libraries.meditations;
  const mantras = libraries.mantras;
  const toolkit = libraries.toolkit;
  const row = 'flex items-center rounded-2xl border text-ink';
  return (
    <div className="mt-[26px] grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
      {meditations.state !== 'off' && (
        <section>
          <SectionHead
            title="Meditate"
            size="column"
            className="pb-3"
            link={{ label: 'See all', to: '/align/meditations' }}
          />
          <div className="flex flex-col gap-2.5">
            {meditations.state === 'locked' ? (
              <LockTile collection={meditations} to="/align/meditations" noun="Meditation" />
            ) : (
              meditations.items.slice(0, 3).map((m) => (
                <Link
                  key={m.id}
                  to={`/align/meditations/${encodeURIComponent(m.id)}`}
                  className={cn(
                    row,
                    'bg-meditate-wash gap-3.5 border-verde/[0.12] px-3.5 py-3',
                    focusRing,
                  )}
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-verde/30 bg-white/50">
                    <span className="h-[26px] w-[26px] rounded-full border border-verde/30" />
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium">{m.title}</span>
                    <span className={cn(tileMono, 'mt-0.5 text-[0.56rem] text-sage')}>
                      {meditationMeta(m)}
                    </span>
                  </span>
                </Link>
              ))
            )}
          </div>
        </section>
      )}
      {mantras.state !== 'off' && (
        <section>
          <SectionHead
            title="Mantras"
            size="column"
            className="pb-3"
            link={{ label: 'See all', to: '/align/mantras' }}
          />
          <div className="flex flex-col gap-2.5">
            {mantras.state === 'locked' ? (
              <LockTile collection={mantras} to="/align/mantras" noun="Mantras" />
            ) : (
              mantras.items.slice(0, 3).map((m) => (
                <Link
                  key={m.id}
                  to={`/align/mantras/${encodeURIComponent(m.id)}`}
                  className={cn(
                    row,
                    'justify-between gap-3 border-verde/[0.14] bg-wash px-4 py-3',
                    focusRing,
                  )}
                >
                  <span className="min-w-0">
                    <span className="block truncate font-display text-[17px] italic text-[#8A7342]">
                      {m.title}
                    </span>
                    <span className="mt-0.5 block truncate text-xs text-ink/75">
                      {mantraIntention(m)}
                    </span>
                  </span>
                  <span className={cn(tileMono, 'shrink-0 text-[0.54rem] text-sage')}>
                    {mantraMeta(m)}
                  </span>
                </Link>
              ))
            )}
          </div>
        </section>
      )}
      <section>
        <SectionHead
          title="Toolkit & Learn"
          size="column"
          className="pb-3"
          link={{ label: 'Browse', to: '/learn' }}
        />
        <div className="flex flex-col gap-2.5">
          {toolkit.state === 'locked' ? (
            <LockTile collection={toolkit} to="/manifest/toolkit" noun="The toolkit" />
          ) : (
            toolkit.items.slice(0, 2).map((p) => (
              <Link
                key={p.id}
                to={`/manifest/toolkit/${encodeURIComponent(p.id)}`}
                className={cn(
                  row,
                  'bg-toolkit-wash justify-between border-gold/25 px-4 py-3',
                  focusRing,
                )}
              >
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium">{p.title}</span>
                  <span className={cn(tileMono, 'mt-0.5 text-[0.56rem] text-[#8A7342]')}>
                    {p.minutes} min
                  </span>
                </span>
                <StarGlyph size={18} />
              </Link>
            ))
          )}
          <CourseList />
        </div>
      </section>
    </div>
  );
}

// ── Manifest ────────────────────────────────────────────────────────────────

/** The Manifest hub's practices as glyph tiles: verde and gold in turn. */
const MANIFEST = [
  { glyph: 'J', title: 'Journal', to: '/manifest/journal' },
  { glyph: 'A', title: 'Affirmations', to: '/manifest/affirmations' },
  { glyph: '369', title: '369 practice', to: '/manifest/369' },
  { glyph: 'V', title: 'Vision board', to: '/manifest/vision-board' },
  { glyph: 'W', title: 'Miracle wins', to: '/manifest/wins' },
  { glyph: '7', title: "Today's alignment", to: '/manifest/todos' },
  { glyph: 'F', title: 'Future self', to: '/manifest/future-self' },
] as const;

export function ManifestTiles({ layout }: { layout: 'phone' | 'desktop' }) {
  const phone = layout === 'phone';
  const tiles = MANIFEST.map((m, i) => {
    const gold = i % 2 === 1;
    return (
      <Link
        key={m.to}
        to={m.to}
        className={cn(
          'relative overflow-hidden border border-border bg-surface text-ink',
          phone ? 'h-28 w-28 rounded-2xl' : 'h-[120px] rounded-[18px]',
          focusRing,
        )}
      >
        <span
          aria-hidden
          className={cn(
            'absolute -right-5 -top-5 rounded-full',
            phone ? 'h-20 w-20' : 'h-[84px] w-[84px]',
            gold ? 'bg-gold/[0.16]' : 'bg-verde/[0.12]',
          )}
        />
        <span
          className={cn(
            'absolute font-display font-medium italic leading-none',
            phone ? 'left-3 top-2.5 text-[30px]' : 'left-3.5 top-3 text-[32px]',
            gold ? 'text-gold' : 'text-verde',
          )}
        >
          {m.glyph}
        </span>
        <span
          className={cn(
            'absolute font-medium leading-tight',
            phone
              ? 'bottom-2.5 left-3 right-2.5 text-xs'
              : 'bottom-3 left-3.5 right-2.5 text-[13px]',
          )}
        >
          {m.title}
        </span>
      </Link>
    );
  });
  return (
    <section className={phone ? 'mt-[22px]' : 'mt-[26px]'}>
      <SectionHead
        title="Manifest"
        size={phone ? 'phone' : 'desktop'}
        className={phone ? 'px-5' : 'pb-3'}
        link={{ label: 'All practices', to: '/manifest' }}
      />
      {phone ? (
        <Rail gap="sm">{tiles}</Rail>
      ) : (
        <div className="grid grid-cols-4 gap-3 xl:grid-cols-7">{tiles}</div>
      )}
    </section>
  );
}

// ── Learn ───────────────────────────────────────────────────────────────────

/** The member's courses, first page only, from the same cache as the Learn tab. */
function useMyCoursesPreview(): MyCourse[] {
  const q = useInfiniteQuery({
    queryKey: qk.myCourses,
    queryFn: ({ pageParam }) => learn.myCourses(pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    staleTime: 60_000,
    retry: false,
  });
  const now = Date.now();
  return (q.data?.pages[0]?.items ?? []).filter(
    (c) => c.revokedAt == null && (c.expiresAt == null || new Date(c.expiresAt).getTime() > now),
  );
}

const courseMeta = (c: MyCourse) =>
  [
    `${c.completedCount} of ${c.lessonCount} lessons`,
    c.resumeLessonTitle && !c.completed ? `Next: ${c.resumeLessonTitle}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

const pct = (c: MyCourse) => Math.max(0, Math.min(100, Math.round(c.progressPct)));

function ProgressBar({ course }: { course: MyCourse }) {
  return (
    <span className="mt-2.5 block h-1 rounded bg-[#EFEDE4]" role="presentation">
      <span className="block h-1 rounded bg-verde" style={{ width: `${pct(course)}%` }} />
    </span>
  );
}

/** Phone: course cards with their progress, then the way into the Shop side. */
export function LearnRail() {
  const courses = useMyCoursesPreview();
  return (
    <section className="mt-[22px]">
      <SectionHead
        title="Learn"
        className="px-5"
        link={{
          label: courses.length > 0 ? 'My courses' : 'Browse courses',
          to: courses.length > 0 ? '/learn' : '/learn/browse',
        }}
      />
      <Rail>
        {courses.slice(0, 6).map((c) => (
          <Link
            key={c.courseId}
            to={`/learn/${c.courseId}`}
            className={cn(
              'w-56 overflow-hidden rounded-[18px] border border-border bg-surface text-ink',
              focusRing,
            )}
          >
            <span className="bg-forest-tile relative block h-[84px]">
              <span
                className={cn(
                  tileMono,
                  'absolute bottom-2.5 left-3.5 text-[0.56rem] tracking-[0.14em] text-paper/85',
                )}
              >
                {c.completed ? 'Course · Complete' : 'Course'}
              </span>
            </span>
            <span className="block px-3.5 pb-3.5 pt-3">
              <span className="line-clamp-2 text-sm font-medium leading-snug">{c.productName}</span>
              <span className="mt-1 line-clamp-2 text-xs text-sage-deep">{courseMeta(c)}</span>
              <ProgressBar course={c} />
            </span>
          </Link>
        ))}
        {courses.length === 0 && (
          <Link
            to="/learn/browse"
            className={cn(
              'flex w-[150px] flex-col items-start justify-end rounded-[18px] border border-dashed border-[rgba(16,32,26,0.25)] bg-surface p-3.5 text-ink',
              focusRing,
            )}
          >
            <Play className="h-[22px] w-[22px] text-verde" strokeWidth={1.5} aria-hidden />
            <span className="mt-2.5 block text-sm font-medium">Find a course</span>
            <span className="mt-0.5 block text-xs text-sage-deep">Browse the academy</span>
          </Link>
        )}
        <Link
          to="/shop"
          className={cn(
            'flex w-[150px] flex-col items-start justify-end rounded-[18px] border border-dashed border-[rgba(16,32,26,0.25)] bg-surface p-3.5 text-ink',
            focusRing,
          )}
        >
          <ShoppingBag className="h-[22px] w-[22px] text-verde" strokeWidth={1.5} aria-hidden />
          <span className="mt-2.5 block text-sm font-medium">Shop &amp; orders</span>
          <span className="mt-0.5 block text-xs text-sage-deep">Wallet · Network · Codes</span>
        </Link>
      </Rail>
    </section>
  );
}

/** Desktop: up to two courses as compact rows under the toolkit. */
function CourseList() {
  const courses = useMyCoursesPreview();
  return (
    <>
      {courses.slice(0, 2).map((c) => (
        <Link
          key={c.courseId}
          to={`/learn/${c.courseId}`}
          className={cn(
            'flex items-center gap-3.5 rounded-2xl border border-border bg-surface py-2.5 pl-2.5 pr-3.5 text-ink',
            focusRing,
          )}
        >
          <span aria-hidden className="bg-forest-tile h-[54px] w-[54px] shrink-0 rounded-xl" />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium">{c.productName}</span>
            <span className="mt-0.5 block truncate text-xs text-sage-deep">
              {c.completedCount} of {c.lessonCount} lessons
            </span>
            <ProgressBar course={c} />
          </span>
        </Link>
      ))}
    </>
  );
}
